import { z } from 'zod/v4';
import {
  FullCompanySchema,
  UserCompanyListRowSchema,
} from '../../../../companies/index.js';
import { EnabledPlatformFeatureSchema } from '../../../../platform-features/index.js';

export const AdminUserGetParamsSchema = z.object({
  id: z.string().min(1),
});

export type AdminUserGetParams = z.infer<typeof AdminUserGetParamsSchema>;

const EnabledDevelopmentTagSchema = z.object({
  featureId: z.string(),
  tag: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isEnabled: z.number(),
});

/**
 * Row shape returned by the admin get-user-by-id endpoint. `looseObject`
 * because the underlying legacy `users` table has ~25 columns and this
 * response is stored verbatim as the frontend's admin user-edit/view state —
 * adding strict typing here would duplicate that shape without buying
 * validation strength. Mirrors `AdminUserImpersonateResponseSchema`, except
 * `company` is nullable rather than optional here: a companyless user (no
 * resolvable primary company) is a valid response, not a 404, and the
 * service always sets the key to `null` rather than omitting it.
 */
export const AdminUserGetResponseSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  primaryCompanyId: z.string().nullable(),
  /** Does this user hold any role at all in the retraced staff tenant? */
  isRetracedStaff: z.boolean(),
  /** Every IAM role key this user holds in their primary company. */
  roleKeys: z.array(z.string()).optional(),
  companyId: z.string().nullable(),
  company: FullCompanySchema.extend({
    enabledFeatures: z.array(EnabledPlatformFeatureSchema),
  }).nullable(),
  userCompanies: z.array(UserCompanyListRowSchema),
  appFeatures: z.array(z.unknown()),
  preferences: z.record(z.string(), z.unknown()),
  enabledDevelopmentTags: z.array(EnabledDevelopmentTagSchema),
});

export type AdminUserGetResponse = z.infer<typeof AdminUserGetResponseSchema>;
